<template>
    <!--
        Tools component.
        Displays a tools overlay
        Closable by clicking button close (x) or by finishing the process
    -->
    <div class="tools" @keydown.esc="close">
        <div class="tools-tabs" v-if="$onduro.hasRole('planner') && initialized">
            <a v-for="tab in tabs" href="#" @click.prevent="toggleActive(tab.name)" :class="[(tab.name === activeTab) ? 'is-active' : '', tab.class ? tab.class : '']" v-if="tab.show">
                <icon :name="tab.icon"/>
            </a>
            <a :class="['tools-close', 'is-trash', visible > 7 ? 'has-shadow' : '']" href="#" @click.prevent="trash" v-if="showTrash"><icon name="delete" /></a>
            <a :class="['tools-close', visible > 7 ? 'has-shadow' : '']" href="#" @click.prevent="close" v-else><icon name="close" /></a>
        </div>

        <!-- display tools for planner -->
        <component :is="'tab-' + activeTab" :item="payload" :date="payload" v-if="$onduro.hasRole('planner')" />

        <!-- display tools for employee -->
        <employee :item="payload" v-else></employee>
    </div>
</template>

<script>
import events from '../../events/events';
import shared from '../../pages/planning/shared';

export default
{
    name: 'Tools',

    props: ['payload', 'replacement'],

    data() {
        return {
            tabs: [],
            activeTab: 'times',
            mobileAdd: false,
            shared: shared.state,
            initialized: false,
            showTrash: false,
        }
    },

    created()
    {
        this.init();

        // $listen will make sure that event is not being registered multiple times
        events.$listen('toolbox.tab', tab =>
        {
            this.setTab(tab);
        });

        window.addEventListener('keydown', this.keyHandler, false);
        window.addEventListener('keyup', this.keyHandler, false);
    },

    beforeDestroy()
    {
        window.removeEventListener('keydown', this.keyHandler, false);
        window.removeEventListener('keyup', this.keyHandler, false);
    },

    watch:
    {
        payload()
        {
            this.init();
        }
    },

    computed:
    {
        activeDuties()
        {
            return _.filter(this.shared.duties, {trashed: false});
        },

        activeShifts()
        {
            return _.filter(this.shared.shifts, {trashed: false});
        },

        showUsers()
        {
            if (this.payload.resource || ! this.$onduro.getConfig('restrict.planner')) return true;
            if (this.payload.applications.length) return true;
            return false;
        },

        // number of visible tabs
        visible()
        {
            if ( ! this.tabs.length) return 0;

            return _.reduce(this.tabs, (count, tab) =>
            {
                return tab.show ? count + 1 : count;
            }, 0);
        },
    },

    methods:
    {
        init: function()
        {
            this.initialized = false;

            // reset tab
            this.activeTab = 'times';

            let item = this.payload;

            this.mobileAdd = (typeof item == 'object') ? false : true;

            if (this.mobileAdd)
            {
                this.tabs =
                [
                    {
                        name: 'new-user',
                        icon: 'person',
                        show: this.showUsers,
                    },
                    {
                        name: 'new-shift',
                        icon: 'menu',
                        show: this.$onduro.hasModule('shifts') && ! this.activeDuties.length,
                    },
                    {
                        name: 'new-duty',
                        icon: 'briefcase',
                        show: this.activeDuties.length,
                    },
                ];

                this.activeTab = 'new-user';
            }
            else
            {
                this.tabs = [
                {
                    name: 'flags',
                    icon: 'warning',
                    show: item.flag == 'signout',
                },
                {
                    name: 'conflicts',
                    icon: 'warning',
                    show: item.conflicts,
                },
                {
                    name: 'times',
                    icon: 'clock',
                    show: true,
                },
                {
                    name: 'shift',
                    icon: 'shifts',
                    show: this.$onduro.hasModule('shifts') && this.activeShifts.length,
                },
                {
                    name: 'duty',
                    icon: 'briefcase',
                    show: this.activeDuties.length, // TODO: might need to check here already, if a legacy/trashed duty has previously been set on the item, then we might need to show duty selection here
                },
                {
                    name: 'user',
                    icon: item.resource ? 'car' : 'person',
                    show: this.showUsers,
                },
                {
                    name: 'tags',
                    icon: 'tag',
                    show: this.$onduro.hasModule('tags') && ! item.resource,
                },
                {
                    name: 'notes',
                    icon: 'attach',
                    show: true,
                },
                {
                    name: 'internal',
                    icon: 'eye_off',
                    show: true,
                },
                {
                    name: 'sick',
                    icon: 'ambulance',
                    show: this.$onduro.hasModule('sickness') && item.assigned && ! item.resource,
                },
                {
                    name: 'delete',
                    icon: 'delete',
                    show: this.$store.state.app.touchDevice,
                    class: 'tab-delete'
                }];

                // no other tab has been set
                if ( ! this.replacement && this.activeTab == 'times')
                {
                    // set default tab
                    let tab = 'times';

                    if ( ! item.assigned) this.activeTab = 'user';

                    if (_.size(this.shared.shifts) && ! item.shift && ! item.times) tab = 'shift';

                    if (_.size(this.shared.duties) && ! item.duty) tab = 'duty';

                    if (item.times || item.shift) tab = 'user';

                    // set tab to flags, if action is required!
                    if (item.flag == 'signout') tab = 'flags';

                    if (item.flag == 'sick') tab = 'sick';

                    // does item has conflict?
                    if (item.conflicts) tab = 'conflicts';

                    this.setTab(tab);
                }
            }

            this.initialized = true;
        },

        setTab(tab)
        {
            // check if tab is available at all
            tab = _.find(this.tabs, {name: tab});

            if (tab && tab.show)
            {
                this.activeTab = tab.name;
            }
            else
            {
                // it's always safe to set the active tab to "times"
                this.activeTab = 'times';
            }
        },

        keyHandler(e)
        {
            if (e.keyCode == 18)
            {
                this.showTrash = (e.type == 'keydown') ? true : false;
            }
        },

        toggleActive(type)
        {
            if (type == 'delete')
            {
                this.deleteItem(this.payload);
                return;
            }

            this.activeTab = type;
        },

        trash()
        {
            this.forceDelete(this.payload);
        },

        deleteItem(item)
        {
            this.$confirm.show(this.$t('tools.remove_item.headline'), this.$t('tools.remove_item.text'),
            {
                confirm: () =>
                {
                    this.forceDelete(item);
                }
            });
        },

        forceDelete(item)
        {
            this.$http.delete('/api/items/' + item.id)
                .then(response =>
                {
                    let key = _.findIndex(this.shared.items, {id: item.id});

                    events.$emit('recalculate', item.date);

                    // remove item
                    this.$delete(this.shared.items, key);

                    this.$tools.close();
                })
                .catch(error =>
                {
                    switch (error.response.data.error)
                    {
                        case 'ERR_CONFIRMED':
                            // hours have already been entered and confirmed
                            this.$modal.show(this.$t('page.roster.item.delete.error.headline'), this.$t('page.roster.move.error.ERR_CONFIRMED'));

                            this.$tools.close();
                        break;

                        case 'ERR_OPEN_CHECKIN':
                            // item has an open checkin, needs to be checked-out first
                            this.$modal.show(this.$t('page.roster.item.delete.error.headline'), this.$t('page.roster.move.error.ERR_OPEN_CHECKIN'));

                            this.$tools.close();
                        break;

                        case 'ERR_HOURS':
                            this.$confirm.show(this.$t('page.roster.item.has_hours.headline'), this.$t('page.roster.item.delete.ERR_HOURS'),
                            {
                                confirm: () =>
                                {
                                    this.$http.delete('/api/items/' + item.id, {
                                        params: {
                                            force: true
                                        }
                                    })
                                    .then(response =>
                                    {
                                        let key = _.findIndex(this.shared.items, {id: item.id});

                                        // remove item
                                        this.$delete(this.shared.items, key);

                                        this.$tools.close();
                                    });
                                }
                            });
                        break;

                        case "ERR_HOURS_CONFIRMED":
                            // make this a confirmation!
                            this.$confirm.show(this.$t('page.roster.item.has_hours.headline'), this.$t('page.roster.item.delete.ERR_CONFIRMED'),
                            {
                                confirm: () =>
                                {
                                    this.$http.delete('/api/items/' + item.id, {
                                        params: {
                                            force: true
                                        }
                                    })
                                    .then(response =>
                                    {
                                        let key = _.findIndex(this.shared.items, {id: item.id});

                                        // remove item
                                        this.$delete(this.shared.items, key);

                                        this.$tools.close();
                                    });
                                }
                            });
                        break;
                    }
                });
        },

        close()
        {
            events.$emit('toolbox.close');
        }
    },

    components:
    {
        tabNotes: require('./tools/Notes'),
        tabInternal: require('./tools/Internal'),
        tabConflicts: require('./tools/Conflicts'),
        tabTimes: require('./tools/Times'),
        tabShift: require('./tools/Shift'),
        tabDuty: require('./tools/Duty'),
        tabUser: require('./tools/User'),
        tabTags: require('./tools/Tags'),
        tabSick: require('./tools/Sick'),
        tabFlags: require('./tools/Flags'),
        employee: require('./tools/Employee'),
        tabNewShift: require('./tools/NewShift'),
        tabNewDuty: require('./tools/NewDuty'),
        tabNewUser: require('./tools/NewUser'),
    }
}
</script>



// WEBPACK FOOTER //
// Tools.vue?7078bcbe